import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Beaker, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/products`).then(res => {
      setProducts(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const filtered = products.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.cas_number.toLowerCase().includes(search.toLowerCase()) ||
    p.tagline.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div data-testid="products-page" className="min-h-screen bg-white">
      <Helmet>
        <title>Peptide API Catalog | Biolyn Pharmaceutical B.V.</title>
        <meta name="description" content="Browse 37+ high-purity peptide APIs from Biolyn Pharmaceutical B.V. including Semaglutide, Tirzepatide, BPC-157, GHK-Cu, and more. European B2B supplier with >99% HPLC-verified purity." />
        <meta property="og:title" content="Peptide API Catalog | Biolyn Pharmaceutical B.V." />
        <meta property="og:description" content="Browse 37+ high-purity peptide APIs. European B2B supplier with >99% HPLC-verified purity." />
        <link rel="canonical" href="https://biolynpharma.com/products" />
      </Helmet>

      <Navbar />

      <div className="pt-24 pb-24 max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24">
        {/* Header */}
        <div className="mb-12">
          <span
            className="text-xs tracking-widest uppercase text-[#0A7C84] mb-3 block"
            style={{ fontFamily: 'var(--font-mono)' }}
          >
            Full Catalog
          </span>
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#0A1128] mb-4"
            style={{ fontFamily: 'var(--font-heading)', fontWeight: 900 }}
            data-testid="products-page-heading"
          >
            Peptide APIs
          </h1>
          <p
            className="text-base md:text-lg text-[#495057] max-w-2xl mb-8"
            style={{ fontFamily: 'var(--font-body)' }}
          >
            {products.length}+ analytically verified, batch-consistent compounds for pharmaceutical development, research, and cosmetic formulation.
          </p>

          {/* Search */}
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A0AAB2]" strokeWidth={1.5} />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, CAS number..."
              className="pl-10 rounded-none border-[#DEE2E6] bg-[#F8F9FA] focus:border-[#0A7C84] focus:ring-[#0A7C84]"
              style={{ fontFamily: 'var(--font-body)' }}
              data-testid="products-search"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-2 border-[#0A7C84] border-t-transparent animate-spin" />
          </div>
        ) : (
          <>
            <div className="text-xs text-[#A0AAB2] mb-4" style={{ fontFamily: 'var(--font-mono)' }}>
              {filtered.length} product{filtered.length !== 1 ? 's' : ''}
            </div>

            {/* Products Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 border border-[#DEE2E6]" data-testid="products-grid">
              {filtered.map((product, idx) => (
                <Link
                  key={product.id}
                  to={`/products/${product.slug}`}
                  className={`group block p-6 transition-colors duration-150 hover:bg-[#F8F9FA] ${
                    idx % 3 !== 2 ? 'lg:border-r border-[#DEE2E6]' : ''
                  } ${idx % 2 !== 1 ? 'sm:border-r lg:border-r-0 border-[#DEE2E6]' : ''} border-b border-[#DEE2E6]`}
                  data-testid={`product-link-${product.slug}`}
                >
                  {/* Image */}
                  <div className="aspect-[4/3] bg-[#F8F9FA] border border-[#E9ECEF] mb-4 overflow-hidden flex items-center justify-center">
                    {product.image_url ? (
                      <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <Beaker className="w-10 h-10 text-[#DEE2E6]" strokeWidth={1} />
                    )}
                  </div>

                  <div className="flex items-start justify-between mb-2">
                    <h3
                      className="text-base text-[#0A1128] group-hover:text-[#0A7C84] transition-colors duration-150"
                      style={{ fontFamily: 'var(--font-heading)', fontWeight: 700 }}
                    >
                      {product.name}
                    </h3>
                    <ArrowRight className="w-4 h-4 text-[#A0AAB2] group-hover:text-[#0A7C84] transition-all duration-150 group-hover:translate-x-0.5 shrink-0 mt-0.5" strokeWidth={1.5} />
                  </div>

                  <p className="text-xs text-[#6C757D] mb-3 line-clamp-2" style={{ fontFamily: 'var(--font-body)' }}>
                    {product.tagline}
                  </p>

                  <div className="flex items-center gap-3">
                    <span className="text-[10px] text-[#A0AAB2]" style={{ fontFamily: 'var(--font-mono)' }}>
                      CAS: {product.cas_number}
                    </span>
                    <Badge
                      variant="outline"
                      className="text-[#0A7C84] border-[#0A7C84]/20 rounded-none text-[10px]"
                      style={{ fontFamily: 'var(--font-mono)' }}
                    >
                      {product.purity}
                    </Badge>
                  </div>
                </Link>
              ))}
            </div>

            {filtered.length === 0 && (
              <div className="text-center py-16 border border-[#DEE2E6]">
                <p className="text-sm text-[#6C757D]" style={{ fontFamily: 'var(--font-body)' }}>
                  No products match your search.
                </p>
              </div>
            )}
          </>
        )}
      </div>

      <Footer />
    </div>
  );
}
